/**
 * CartViewEnhancer
 * 
 * Adds visual indicators to cart items based on validation results.
 * Handles greying out unavailable items and showing date conflict messages.
 * 
 * Requirements: 3.1, 3.2, 3.3, 5.3, 5.4
 */

class CartViewEnhancer {
  constructor() {
    this.messageContainerId = 'cart-availability-messages';
  }

  /**
   * Applies availability styling to cart items
   * @param {Object} validationResult - Result from CartValidator
   * Requirements: 3.1, 3.2, 3.3
   */
  applyAvailabilityStyles(validationResult) {
    // Clear existing indicators first
    this.clearAvailabilityIndicators();

    if (!validationResult) {
      console.warn('[CartViewEnhancer] No validation result provided');
      return;
    }

    // Apply unavailable styling to items
    if (validationResult.unavailable && validationResult.unavailable.length > 0) {
      validationResult.unavailable.forEach(item => {
        this._markItemUnavailable(item);
      });

      // Show date conflict message
      this.showDateConflictMessage(validationResult.unavailable);
    }

    // Ensure available items have normal styling (remove any unavailable classes)
    if (validationResult.available && validationResult.available.length > 0) {
      validationResult.available.forEach(item => {
        this._markItemAvailable(item);
      });
    }
  }

  /**
   * Displays date conflict message showing unavailable items and their date ranges
   * @param {Array} unavailableItems - Items with availability info
   * Requirements: 5.3, 5.4
   */
  showDateConflictMessage(unavailableItems) {
    if (!unavailableItems || unavailableItems.length === 0) {
      return;
    }

    const container = this._getOrCreateMessageContainer();
    
    // Create conflict banner
    const banner = document.createElement('div');
    banner.className = 'cart__date-conflict-banner';
    banner.setAttribute('data-availability-message', 'true');

    // Create message content
    let messageHTML = '<strong>Some items are not available for the selected date:</strong><ul>';
    
    unavailableItems.forEach(item => {
      const dateRange = this._formatDateRange(item.availableFrom, item.availableTo);
      messageHTML += `<li>${item.title}${dateRange ? ` - Available: ${dateRange}` : ''}</li>`;
    });
    
    messageHTML += '</ul>';
    banner.innerHTML = messageHTML;

    container.appendChild(banner);
  }

  /**
   * Removes all availability indicators
   * Requirements: 3.5
   */
  clearAvailabilityIndicators() {
    // Remove unavailable classes from all cart items
    const unavailableItems = document.querySelectorAll('.cart-item--unavailable');
    unavailableItems.forEach(item => {
      item.classList.remove('cart-item--unavailable');
    });

    // Remove all availability messages
    const messages = document.querySelectorAll('[data-availability-message]');
    messages.forEach(msg => msg.remove());

    // Clear message container
    const container = document.getElementById(this.messageContainerId);
    if (container) {
      container.innerHTML = '';
    }
  }

  /**
   * Marks a cart item as unavailable with greyed-out styling and message
   * @private
   */
  _markItemUnavailable(item) {
    const cartItemElement = this._findCartItemElement(item.key || item.variantId);
    
    if (cartItemElement) {
      // Add unavailable class for greyed-out styling
      cartItemElement.classList.add('cart-item--unavailable');

      // Add availability message
      const existingMessage = cartItemElement.querySelector('.cart-item__availability-message');
      if (!existingMessage) {
        const message = document.createElement('div');
        message.className = 'cart-item__availability-message';
        message.setAttribute('data-availability-message', 'true');
        message.textContent = 'Not available for selected date';

        // Insert message after item title or at the end of item
        const titleElement = cartItemElement.querySelector('.cart-item__name, .cart-item__title, h3, h4');
        if (titleElement) {
          titleElement.parentNode.insertBefore(message, titleElement.nextSibling);
        } else {
          cartItemElement.appendChild(message);
        }
      }
    }
  }

  /**
   * Marks a cart item as available with normal styling
   * @private
   */
  _markItemAvailable(item) {
    const cartItemElement = this._findCartItemElement(item.key || item.variantId);
    
    if (cartItemElement) {
      // Remove unavailable class
      cartItemElement.classList.remove('cart-item--unavailable');

      // Remove any availability messages
      const messages = cartItemElement.querySelectorAll('.cart-item__availability-message');
      messages.forEach(msg => msg.remove());
    }
  }

  /**
   * Finds the DOM element for a cart item
   * @private
   */
  _findCartItemElement(identifier) {
    // Try multiple selectors to find cart item
    const selectors = [
      `[data-cart-item-key="${identifier}"]`,
      `[data-variant-id="${identifier}"]`,
      `[data-key="${identifier}"]`,
      `.cart-item[data-index*="${identifier}"]`
    ];

    for (const selector of selectors) {
      const element = document.querySelector(selector);
      if (element) {
        return element;
      }
    }

    // Fallback: try to find by variant ID in the key attribute
    const allCartItems = document.querySelectorAll('.cart-item, [class*="cart-item"]');
    for (const item of allCartItems) {
      const key = item.getAttribute('data-cart-item-key') || item.getAttribute('data-key');
      if (key && key.includes(identifier)) {
        return item;
      }
    }

    console.warn(`[CartViewEnhancer] Could not find cart item element for: ${identifier}`);
    return null;
  }

  /**
   * Gets or creates the message container
   * @private
   */
  _getOrCreateMessageContainer() {
    let container = document.getElementById(this.messageContainerId);
    
    if (!container) {
      container = document.createElement('div');
      container.id = this.messageContainerId;
      
      // Try to insert at the beginning of cart
      const cartElement = document.querySelector('.cart, .cart-drawer, [data-cart], main');
      if (cartElement) {
        cartElement.insertBefore(container, cartElement.firstChild);
      } else {
        console.warn('[CartViewEnhancer] Could not find cart element to insert message container');
        document.body.appendChild(container);
      }
    }
    
    return container;
  }

  /**
   * Formats date range for display
   * @private
   */
  _formatDateRange(availableFrom, availableTo) {
    if (!availableFrom && !availableTo) {
      return '';
    }

    const formatDate = (dateStr) => {
      if (!dateStr) return null;
      try {
        const date = new Date(dateStr);
        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
      } catch (e) {
        return dateStr;
      }
    };

    const fromFormatted = formatDate(availableFrom);
    const toFormatted = formatDate(availableTo);

    if (fromFormatted && toFormatted) {
      return `${fromFormatted} - ${toFormatted}`;
    } else if (fromFormatted) {
      return `from ${fromFormatted}`;
    } else if (toFormatted) {
      return `until ${toFormatted}`;
    }

    return '';
  }
}

// Export for use in other modules
if (typeof module !== 'undefined' && module.exports) {
  module.exports = CartViewEnhancer;
}
